import { useState, useEffect } from "react";
import { API_BASE } from "../api/config";


const CURRENT_YEAR = new Date().getFullYear();

export default function ZaehlerstandPage() {
  const [objektId, setObjektId] = useState("");
  const [jahr, setJahr]         = useState("");
  const [art, setArt]             = useState("");
  const [loading, setLoading]     = useState(false);
  const [toast, setToast]         = useState(null);
  const [templateOk, setTemplateOk] = useState(null); // null=unbekannt, true, false
  const [setupRunning, setSetupRunning] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/zaehlerstand/vba/template-status`)
      .then(r => r.json())
      .then(d => setTemplateOk(d.exists))
      .catch(() => setTemplateOk(false));
  }, []);

  function showToast(msg, ok = true) {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 5000);
  }

  async function runSetup() {
    setSetupRunning(true);
    showToast("Setup läuft… Excel öffnet sich kurz, bitte warten.", true);
    try {
      const res = await fetch(`${API_BASE}/zaehlerstand/vba/setup-template`, { method: "POST" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Fehler");
      setTemplateOk(true);
      showToast("✓ Setup fertig! Jetzt kannst du die .xlsm herunterladen.");
    } catch (e) {
      showToast("Setup fehlgeschlagen: " + e.message, false);
    } finally {
      setSetupRunning(false);
    }
  }

  async function downloadWorkbook() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (objektId.trim()) params.set("objekt_id", objektId.trim());
      if (jahr.trim())     params.set("jahr",      jahr.trim());
      if (art.trim())      params.set("art",        art.trim());

      const url = `${API_BASE}/zaehlerstand/vba/workbook` +
                  (params.toString() ? "?" + params.toString() : "");

      const res = await fetch(url);
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "HTTP " + res.status);
      }
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "Zaehlerstaende_Sync.xlsm";
      link.click();
      URL.revokeObjectURL(link.href);
      showToast("✓ Zaehlerstaende_Sync.xlsm heruntergeladen");
    } catch (e) {
      showToast("Fehler: " + e.message, false);
    } finally {
      setLoading(false);
    }
  }

  const inputStyle = {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid var(--color-gray-300, #ccc)",
    background: "var(--bg-input, #fff)",
    color: "var(--text-primary, #111)",
    fontSize: 14,
    width: "100%",
    boxSizing: "border-box",
  };

  const labelStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: "var(--text-secondary, #666)",
    marginBottom: 4,
    display: "block",
  };

  return (
    <div style={{ maxWidth: 700, margin: "40px auto", padding: "0 20px" }}>

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", top: 20, right: 20, zIndex: 9999,
          background: toast.ok ? "#2e7d32" : "#c62828",
          color: "#fff", padding: "12px 20px", borderRadius: 10,
          boxShadow: "0 4px 16px rgba(0,0,0,0.2)", fontSize: 14,
        }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>
          Zählerstand – Excel VBA Sync
        </h2>
        <p style={{ margin: "6px 0 0", color: "var(--text-secondary, #666)", fontSize: 14 }}>
          Einmalig Setup klicken, danach immer direkt <code className="zs-code">.xlsm</code> herunterladen — VBA ist bereits drin.
        </p>
      </div>

      {/* Setup-Block */}
      <div style={{
        background: templateOk ? "rgba(46,125,50,0.08)" : "rgba(198,100,0,0.08)",
        border: `1px solid ${templateOk ? "rgba(46,125,50,0.3)" : "rgba(198,100,0,0.3)"}`,
        borderRadius: 14, padding: 20, marginBottom: 24,
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
      }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 15 }}>
            {templateOk === null ? "⏳ Status wird geprüft…"
              : templateOk ? "✓ VBA-Template vorhanden"
              : "⚠ Setup noch nicht ausgeführt"}
          </div>
          <div style={{ fontSize: 13, color: "var(--text-secondary, #666)", marginTop: 4 }}>
            {templateOk
              ? "Du kannst die .xlsm direkt herunterladen — VBA ist eingebettet."
              : "Einmalig ausführen. Excel öffnet sich kurz und erstellt das Template."}
          </div>
        </div>
        {!templateOk && (
          <button
            onClick={runSetup}
            disabled={setupRunning || templateOk === null}
            style={{
              padding: "10px 20px", borderRadius: 9, border: "none",
              background: setupRunning ? "#888" : "#e65100",
              color: "#fff", fontWeight: 600, fontSize: 14,
              cursor: setupRunning ? "not-allowed" : "pointer", whiteSpace: "nowrap",
            }}
          >
            {setupRunning ? "⏳ Läuft…" : "Setup ausführen (einmalig)"}
          </button>
        )}
      </div>

      {/* Karte: Download */}
      <div style={{
        background: "var(--bg-card, #fff)",
        border: "1px solid var(--color-gray-200, #e0e0e0)",
        borderRadius: 14,
        padding: 24,
        marginBottom: 24,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}>
        <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 600 }}>
          Schritt 1 – Excel herunterladen
        </h3>

        {/* Filter (optional) */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
          <div>
            <label style={labelStyle}>Objekt-ID (optional)</label>
            <input
              style={inputStyle}
              value={objektId}
              onChange={e => setObjektId(e.target.value)}
              placeholder="z.B. 084"
            />
          </div>
          <div>
            <label style={labelStyle}>Jahr (optional)</label>
            <input
              style={inputStyle}
              value={jahr}
              onChange={e => setJahr(e.target.value)}
              placeholder={String(CURRENT_YEAR)}
              type="number"
            />
          </div>
          <div>
            <label style={labelStyle}>Art (optional)</label>
            <select
              style={{ ...inputStyle, cursor: "pointer" }}
              value={art}
              onChange={e => setArt(e.target.value)}
            >
              <option value="">Alle</option>
              <option value="Strom">Strom</option>
              <option value="Gas">Gas</option>
              <option value="Wasser">Wasser</option>
            </select>
          </div>
        </div>

        <button
          onClick={downloadWorkbook}
          disabled={loading}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "11px 24px",
            background: loading ? "#888" : "#2B579A",
            color: "#fff", border: "none", borderRadius: 9,
            fontSize: 15, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer",
            transition: "background 0.2s",
          }}
        >
          {loading ? (
            <>
              <span style={{ width: 16, height: 16, border: "2px solid #fff3", borderTop: "2px solid #fff", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
              Wird geladen…
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Zaehlerstaende_Sync.xlsx herunterladen
            </>
          )}
        </button>
      </div>

      {/* Info-Box */}
      <div className="zs-info-box">
        <strong>Editierbare Spalten (hellgelb):</strong> Stand · Einheit · Status · Notiz<br />
        <strong>Schreibgeschützt (grau):</strong> ID · Objekt · Adresse · Zähler-Nr · Art · Lieferant · Foto-Datum<br />
        <strong>Buttons in Excel:</strong> <code className="zs-code">DatenHolen</code> und <code className="zs-code">DatenSenden</code> — bereits im VBA-Template enthalten.
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .zs-code {
          background: rgba(128, 160, 255, 0.15);
          color: var(--text-primary, inherit);
          padding: 1px 6px;
          border-radius: 4px;
          font-family: monospace;
          font-size: 0.9em;
          border: 1px solid rgba(128, 160, 255, 0.25);
        }
        .zs-info-box {
          margin-top: 20px;
          padding: 12px 16px;
          background: rgba(43, 87, 154, 0.12);
          border-left: 3px solid #2B579A;
          border-radius: 0 8px 8px 0;
          font-size: 13px;
          color: var(--text-secondary, #888);
          line-height: 1.7;
        }
      `}</style>
    </div>
  );
}
